import { postWcAjax, replaceWooFragments } from './cart-ajax-client';

class ShoppingListsModal {
    constructor() {
        this.triggerSelector = '[data-shopping-lists-trigger][data-product-id]';
        this.activeTrigger = null;
        this.currentProductId = 0;
        this.nonce = '';
        this.quantity = 1;
        this.lists = [];
        this.loading = false;
        this.pendingRemoveProducts = new Set();

        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);

        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    handleDocumentClick(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const trigger = target.closest(this.triggerSelector);
        if (trigger instanceof HTMLElement) {
            event.preventDefault();
            if (this.isTriggerActive(trigger)) {
                this.removeFromLists(trigger);
                return;
            }

            this.openForTrigger(trigger);
            return;
        }

        if (!this.modalRoot) {
            return;
        }

        if (target.closest('[data-shopping-lists-close]')) {
            event.preventDefault();
            this.close();
            return;
        }

        if (target.closest('[data-shopping-lists-qty-step]')) {
            event.preventDefault();
            this.updateQuantity(target.closest('[data-shopping-lists-qty-step]'));
            return;
        }

        if (target.closest('[data-shopping-lists-create-toggle]')) {
            event.preventDefault();
            this.toggleCreateForm();
            return;
        }

        if (target.closest('[data-shopping-lists-create-submit]')) {
            event.preventDefault();
            this.createList();
            return;
        }

        if (target.closest('[data-shopping-lists-submit]')) {
            event.preventDefault();
            this.submitAddToLists();
            return;
        }

        if (
            this.modalRoot.classList.contains('is-open') &&
            !target.closest('[data-shopping-lists-panel]')
        ) {
            this.close();
        }
    }

    handleKeyDown(event) {
        if (event.key === 'Escape' && this.modalRoot?.classList.contains('is-open')) {
            this.close();
        }
    }

    ensureModal() {
        if (this.modalRoot instanceof HTMLElement) {
            return;
        }

        const root = document.createElement('div');
        root.setAttribute('data-shopping-lists-modal', '');
        root.className =
            'fixed inset-0 z-[10060] pointer-events-none opacity-0 transition-opacity duration-200 ease-out';

        root.innerHTML = `
            <div data-shopping-lists-panel class="absolute pointer-events-auto w-[calc(100vw-24px)] max-w-[320px] max-h-[452px] overflow-y-auto rounded-[16px] bg-white p-6 shadow-[0_20px_50px_rgba(0,0,0,0.15)] opacity-0 translate-y-2 scale-[0.98] transition-all duration-220 ease-out">
                <div class="flex items-start justify-between gap-4">
                    <h3 class="text-h6 text-neutral-900">Dodaj do listy zakupowej</h3>
                    <button type="button" data-shopping-lists-close class="inline-flex h-7 w-7 items-center justify-center text-[28px] leading-none text-neutral-900" aria-label="Zamknij">×</button>
                </div>

                <div class="mt-3">
                    <div class="text-p-small text-neutral-500">Wybierz ilość</div>
                    <div class="mt-4 inline-flex h-12 w-full items-center justify-between rounded-[10px] border border-neutral-100 px-3">
                        <button type="button" data-shopping-lists-qty-step="down" class="wc-product-box__qty-button inline-flex h-8 w-8 items-center justify-center cursor-pointer text-neutral-400 transition-colors duration-200 ease-in-out hover:text-neutral-900" aria-label="Zmniejsz ilość">-</button>
                        <input type="number" data-shopping-lists-qty-input class="wc-product-box__qty-input sp-cart-qty-input w-[48px] border-0 bg-transparent text-center font-semibold text-neutral-900 outline-none" value="1" min="1" step="1" inputmode="numeric" />
                        <button type="button" data-shopping-lists-qty-step="up" class="wc-product-box__qty-button is-plus inline-flex h-8 w-8 items-center justify-center cursor-pointer text-neutral-400 transition-colors duration-200 ease-in-out hover:text-neutral-900" aria-label="Zwiększ ilość">+</button>
                    </div>
                </div>

                <div class="mt-6">
                    <div class="text-p-small text-neutral-500">Wybierz listę lub listy</div>
                    <div data-shopping-lists-list class="mt-4 max-h-[118px] overflow-y-auto pr-1 flex flex-col gap-3"></div>

                    <button type="button" data-shopping-lists-create-toggle class="mt-3 text-p-small font-medium text-footer-blue underline underline-offset-2">Stwórz nową listę</button>

                    <div data-shopping-lists-create-wrap class="mt-4 hidden">
                        <div class="mda-input__field">
                            <label class="mda-input__label" for="sp-inline-shopping-list-name">Nazwa nowej listy</label>
                            <div class="relative">
                                <input id="sp-inline-shopping-list-name" type="text" data-shopping-lists-create-input class="mda-input pr-[90px] text-p-regular" placeholder="Nazwa nowej listy" />
                                <button type="button" data-shopping-lists-create-submit class="absolute right-5 top-1/2 -translate-y-1/2 text-p-regular text-footer-blue underline underline-offset-2">Dodaj</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div data-shopping-lists-error class="mt-3 min-h-[18px] text-p-small text-red"></div>

                <button type="button" data-shopping-lists-submit class="mt-1 inline-flex py-3 w-full items-center justify-center rounded-[8px] bg-footer-blue text-p-regular font-semibold text-white">Dodaj</button>
            </div>
        `;

        document.body.appendChild(root);
        this.modalRoot = root;
        this.panel = root.querySelector('[data-shopping-lists-panel]');
        this.qtyValueNode = root.querySelector('[data-shopping-lists-qty-value]');
        this.qtyInputNode = root.querySelector('[data-shopping-lists-qty-input]');
        this.listNode = root.querySelector('[data-shopping-lists-list]');
        this.errorNode = root.querySelector('[data-shopping-lists-error]');
        this.createWrap = root.querySelector('[data-shopping-lists-create-wrap]');
        this.createInput = root.querySelector('[data-shopping-lists-create-input]');
    }

    async openForTrigger(trigger) {
        this.ensureModal();

        const productId = parseInt(trigger.getAttribute('data-product-id') || '0', 10);
        if (productId <= 0) {
            return;
        }

        this.activeTrigger = trigger;
        this.currentProductId = productId;
        this.nonce = trigger.getAttribute('data-shopping-lists-nonce') || '';
        this.quantity = 1;
        this.lists = [];

        this.clearError();
        this.renderQuantity();
        this.renderListOptions();

        this.positionPanel(trigger);
        this.open();

        await this.loadModalData();
    }

    isTriggerActive(trigger) {
        return trigger.getAttribute('data-shopping-lists-active') === '1';
    }

    open() {
        if (!(this.modalRoot instanceof HTMLElement)) {
            return;
        }

        this.modalRoot.classList.add('pointer-events-auto', 'opacity-100', 'is-open');
        this.modalRoot.classList.remove('pointer-events-none', 'opacity-0');
        if (this.panel instanceof HTMLElement) {
            this.panel.classList.remove('opacity-0', 'translate-y-2', 'scale-[0.98]');
            this.panel.classList.add('opacity-100', 'translate-y-0', 'scale-100');
        }
        document.body.classList.add('overflow-hidden');
    }

    close() {
        if (!(this.modalRoot instanceof HTMLElement)) {
            return;
        }

        const root = this.modalRoot;
        const panel = this.panel;

        root.classList.remove('pointer-events-auto', 'opacity-100', 'is-open');
        root.classList.add('pointer-events-none', 'opacity-0');
        if (panel instanceof HTMLElement) {
            panel.classList.remove('opacity-100', 'translate-y-0', 'scale-100');
            panel.classList.add('opacity-0', 'translate-y-2', 'scale-[0.98]');
        }
        document.body.classList.remove('overflow-hidden');

        this.clearError();
        this.toggleCreateForm(false);
        this.activeTrigger = null;
        this.currentProductId = 0;

        window.setTimeout(() => {
            if (root.parentNode) {
                root.parentNode.removeChild(root);
            }
        }, 220);

        this.modalRoot = null;
        this.panel = null;
        this.qtyValueNode = null;
        this.qtyInputNode = null;
        this.listNode = null;
        this.errorNode = null;
        this.createWrap = null;
        this.createInput = null;
    }

    positionPanel(trigger) {
        if (!(this.panel instanceof HTMLElement)) {
            return;
        }

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const panelWidth = Math.min(320, viewportWidth - 24);
        const panelRect = this.panel.getBoundingClientRect();
        const panelHeight = panelRect.height || 452;
        const triggerRect = trigger.getBoundingClientRect();

        const preferredLeft = triggerRect.right - panelWidth + 36;
        const left = Math.max(12, Math.min(viewportWidth - panelWidth - 12, preferredLeft));
        const preferredTop = triggerRect.bottom + 8;
        const fallbackTop = triggerRect.top - panelHeight - 8;
        const top = preferredTop + panelHeight <= viewportHeight - 12
            ? preferredTop
            : Math.max(12, fallbackTop);

        this.panel.style.left = `${left}px`;
        this.panel.style.top = `${top}px`;
    }

    async loadModalData() {
        this.setLoading(true);

        const payload = new URLSearchParams();
        payload.append('nonce', this.nonce);
        payload.append('product_id', String(this.currentProductId));

        try {
            const response = await postWcAjax('sp_shopping_lists_modal_data', payload);

            if (!response?.success) {
                const loginUrl = response?.data?.login_url;
                if (loginUrl) {
                    window.location.href = loginUrl;
                    return;
                }

                this.showError(
                    response?.data?.message ||
                        'Nie udało się pobrać list zakupowych.'
                );
                return;
            }

            this.lists = Array.isArray(response?.data?.lists)
                ? response.data.lists.map(list => ({
                      id: parseInt(list.id || 0, 10),
                      name: String(list.name || ''),
                      checked: Boolean(list.checked),
                  }))
                : [];

            this.setButtonActive(
                this.currentProductId,
                Boolean(response?.data?.contains_any)
            );
            this.renderListOptions();
        } catch (error) {
            this.showError('Nie udało się pobrać list zakupowych.');
        } finally {
            this.setLoading(false);
        }
    }

    renderQuantity() {
        if (this.qtyInputNode instanceof HTMLInputElement) {
            this.qtyInputNode.value = String(this.quantity);
        }
    }

    updateQuantity(button) {
        if (!(button instanceof HTMLElement)) {
            return;
        }

        const step = button.getAttribute('data-shopping-lists-qty-step');
        if (step === 'up') {
            this.quantity += 1;
        }
        if (step === 'down') {
            this.quantity = Math.max(1, this.quantity - 1);
        }

        this.renderQuantity();
    }

    renderListOptions() {
        if (!(this.listNode instanceof HTMLElement)) {
            return;
        }

        if (!this.lists.length) {
            this.listNode.innerHTML =
                '<div class="text-p-regular text-neutral-500">Brak list. Utwórz pierwszą listę.</div>';
            return;
        }

        this.listNode.innerHTML = this.lists
            .map(
                list => `
                <label class="mda-checkbox inline-flex items-center gap-3 text-p-small text-neutral-900 cursor-pointer">
                    <input type="checkbox" class="mda-checkbox__input" data-shopping-lists-checkbox value="${list.id}" ${list.checked ? 'checked' : ''}>
                    <span class="mda-checkbox__box" aria-hidden="true"></span>
                    <span>${this.escapeHtml(list.name)}</span>
                </label>
            `
            )
            .join('');
    }

    toggleCreateForm(force) {
        if (!(this.createWrap instanceof HTMLElement)) {
            return;
        }

        const shouldShow =
            typeof force === 'boolean'
                ? force
                : this.createWrap.classList.contains('hidden');

        this.createWrap.classList.toggle('hidden', !shouldShow);
        this.createWrap.classList.toggle('block', shouldShow);

        if (shouldShow && this.createInput instanceof HTMLInputElement) {
            this.createInput.focus();
        }
    }

    async createList() {
        if (!(this.createInput instanceof HTMLInputElement)) {
            return;
        }

        const name = this.createInput.value.trim();
        if (!name) {
            this.showError('Podaj nazwę listy.');
            return;
        }

        this.setLoading(true);

        const payload = new URLSearchParams();
        payload.append('nonce', this.nonce);
        payload.append('name', name);

        try {
            const response = await postWcAjax('sp_shopping_lists_create', payload);

            if (!response?.success) {
                this.showError(
                    response?.data?.message || 'Nie udało się utworzyć listy.'
                );
                return;
            }

            const list = response?.data?.list || {};
            const listId = parseInt(list.id || 0, 10);
            if (listId <= 0) {
                this.showError('Nie udało się utworzyć listy.');
                return;
            }

            this.lists.push({
                id: listId,
                name: String(list.name || name),
                checked: true,
            });

            this.renderListOptions();
            this.createInput.value = '';
            this.toggleCreateForm(false);
            this.clearError();
        } catch (error) {
            this.showError('Nie udało się utworzyć listy.');
        } finally {
            this.setLoading(false);
        }
    }

    async submitAddToLists() {
        const selectedIds = this.getSelectedListIds();

        if (!selectedIds.length) {
            this.showError('Wybierz co najmniej jedną listę.');
            return;
        }

        this.setLoading(true);

        const payload = new URLSearchParams();
        payload.append('nonce', this.nonce);
        payload.append('product_id', String(this.currentProductId));
        if (this.qtyInputNode instanceof HTMLInputElement) {
            const parsedQty = parseInt(this.qtyInputNode.value || '1', 10);
            this.quantity = Number.isFinite(parsedQty) ? Math.max(1, parsedQty) : 1;
            this.qtyInputNode.value = String(this.quantity);
        }

        payload.append('quantity', String(this.quantity));

        selectedIds.forEach(listId => {
            payload.append('list_ids[]', String(listId));
        });

        try {
            const response = await postWcAjax('sp_shopping_lists_add_to_lists', payload);

            if (!response?.success) {
                this.showError(
                    response?.data?.message || 'Nie udało się dodać produktu.'
                );
                return;
            }

            this.setButtonActive(
                this.currentProductId,
                Boolean(response?.data?.contains_any)
            );

            this.showToast(
                response?.data?.message || 'Dodano produkt do listy zakupowej.',
                'cart'
            );
            this.close();
        } catch (error) {
            this.showError('Nie udało się dodać produktu.');
        } finally {
            this.setLoading(false);
        }
    }

    async removeFromLists(trigger) {
        const productId = parseInt(trigger.getAttribute('data-product-id') || '0', 10);
        const nonce = trigger.getAttribute('data-shopping-lists-nonce') || '';

        if (productId <= 0 || !nonce || this.pendingRemoveProducts.has(productId)) {
            return;
        }

        this.pendingRemoveProducts.add(productId);
        trigger.classList.add('opacity-60');

        const payload = new URLSearchParams();
        payload.append('nonce', nonce);
        payload.append('product_id', String(productId));

        try {
            const response = await postWcAjax('sp_shopping_lists_remove_from_lists', payload);

            if (!response?.success) {
                this.showToast(
                    response?.data?.message || 'Nie udało się usunąć produktu z list.',
                    'cart'
                );
                return;
            }

            this.setButtonActive(productId, false);
            this.showToast(
                response?.data?.message || 'Usunięto produkt z list zakupowych.',
                'cart'
            );
        } catch (error) {
            this.showToast('Nie udało się usunąć produktu z list.', 'cart');
        } finally {
            this.pendingRemoveProducts.delete(productId);
            trigger.classList.remove('opacity-60');
        }
    }

    getSelectedListIds() {
        if (!(this.listNode instanceof HTMLElement)) {
            return [];
        }

        return Array.from(
            this.listNode.querySelectorAll('[data-shopping-lists-checkbox]:checked')
        )
            .map(node => parseInt(node.value || '0', 10))
            .filter(id => id > 0);
    }

    setLoading(loading) {
        this.loading = loading;

        if (!(this.modalRoot instanceof HTMLElement)) {
            return;
        }

        this.modalRoot
            .querySelectorAll(
                'button, input[type="checkbox"], input[type="text"], input[type="number"]'
            )
            .forEach(node => {
                if ('disabled' in node) {
                    node.disabled = loading;
                }
            });
    }

    showError(message) {
        if (this.errorNode instanceof HTMLElement) {
            this.errorNode.textContent = message || '';
        }
    }

    clearError() {
        this.showError('');
    }

    showToast(text, id = 'cart') {
        document.body.dispatchEvent(
            new CustomEvent('sp:toast', {
                detail: {
                    id,
                    text,
                },
            })
        );
    }

    setButtonActive(productId, isActive) {
        document
            .querySelectorAll(
                `${this.triggerSelector}[data-product-id="${productId}"]`
            )
            .forEach(button => {
                const icon = button.querySelector('[data-shopping-lists-icon]');
                if (icon instanceof HTMLElement) {
                    icon.textContent = isActive ? '★' : '☆';
                }

                button.classList.toggle('is-active', isActive);
                button.classList.toggle('text-neutral-400', !isActive);
                button.classList.toggle('hover:text-footer-blue', !isActive);
                button.style.color = isActive ? '#FCBB44' : '';
                button.setAttribute('data-shopping-lists-active', isActive ? '1' : '0');
            });
    }

    escapeHtml(value) {
        return String(value || '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    }
}

new ShoppingListsModal();

class ShoppingListsPageModals {
    constructor() {
        this.pageRoot = document.querySelector('[data-shopping-lists-page]');
        if (!(this.pageRoot instanceof HTMLElement)) {
            return;
        }
        this.ajaxNonce = this.pageRoot.getAttribute('data-shopping-lists-ajax-nonce') || '';
        this.pendingQtyRequests = new Set();
        this.pendingRemoveRequests = new Set();
        this.pendingAddListToCartRequests = new Set();

        this.createModal = document.querySelector('[data-shopping-lists-create-modal]');
        this.createPanel = document.querySelector(
            '[data-shopping-lists-create-modal-panel]'
        );
        this.createInput = document.querySelector(
            '[data-shopping-lists-create-input]'
        );

        this.deleteModal = document.querySelector('[data-shopping-lists-delete-modal]');
        this.deletePanel = document.querySelector(
            '[data-shopping-lists-delete-modal-panel]'
        );
        this.deleteName = document.querySelector('[data-shopping-lists-delete-name]');
        this.deleteListIdInput = document.querySelector(
            '[data-shopping-lists-delete-list-id]'
        );

        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.closeTimers = new WeakMap();

        document.addEventListener('click', this.onClick);
        document.addEventListener('submit', this.onSubmit);
        document.addEventListener('keydown', this.onKeydown);
    }

    onClick(event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const qtyStep = target.closest('[data-shopping-list-qty-step]');
        if (qtyStep instanceof HTMLElement) {
            event.preventDefault();
            this.handleQtyStep(qtyStep);
            return;
        }

        const removeBtn = target.closest('[data-shopping-list-remove]');
        if (removeBtn instanceof HTMLElement) {
            event.preventDefault();
            this.handleRemoveItem(removeBtn);
            return;
        }

        const openCreate = target.closest('[data-shopping-lists-open-create]');
        if (openCreate) {
            event.preventDefault();
            this.openCreateModal();
            return;
        }

        const openDelete = target.closest('[data-shopping-lists-open-delete]');
        if (openDelete) {
            event.preventDefault();

            const listId = parseInt(openDelete.getAttribute('data-list-id') || '0', 10);
            const listName = openDelete.getAttribute('data-list-name') || '';

            this.openDeleteModal(listId, listName);
            return;
        }

        if (target.closest('[data-shopping-lists-page-close]')) {
            event.preventDefault();
            this.closeAll();
            return;
        }

        if (
            this.isOpen(this.createModal) &&
            !target.closest('[data-shopping-lists-create-modal-panel]')
        ) {
            this.closeCreateModal();
            return;
        }

        if (
            this.isOpen(this.deleteModal) &&
            !target.closest('[data-shopping-lists-delete-modal-panel]')
        ) {
            this.closeDeleteModal();
        }
    }

    onKeydown(event) {
        if (event.key !== 'Escape') {
            return;
        }

        this.closeAll();
    }

    onSubmit(event) {
        const target = event.target;
        if (!(target instanceof HTMLFormElement)) {
            return;
        }

        const form = target.closest('[data-shopping-list-add-to-cart-form]');
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        event.preventDefault();
        this.handleAddListToCart(form);
    }

    openCreateModal() {
        this.open(this.createModal);

        if (this.createInput instanceof HTMLInputElement) {
            this.createInput.focus();
        }
    }

    openDeleteModal(listId, listName) {
        if (listId <= 0) {
            return;
        }

        if (this.deleteName instanceof HTMLElement) {
            this.deleteName.textContent = listName;
        }

        if (this.deleteListIdInput instanceof HTMLInputElement) {
            this.deleteListIdInput.value = String(listId);
        }

        this.open(this.deleteModal);
    }

    closeCreateModal() {
        this.close(this.createModal);
    }

    closeDeleteModal() {
        this.close(this.deleteModal);
    }

    closeAll() {
        this.closeCreateModal();
        this.closeDeleteModal();
    }

    async handleQtyStep(button) {
        const row = button.closest('[data-shopping-list-item]');
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const listId = parseInt(row.getAttribute('data-list-id') || '0', 10);
        const productId = parseInt(row.getAttribute('data-product-id') || '0', 10);
        const direction = button.getAttribute('data-shopping-list-qty-step') || '';

        if (!listId || !productId || !this.ajaxNonce) {
            return;
        }

        const requestKey = `${listId}:${productId}`;
        if (this.pendingQtyRequests.has(requestKey)) {
            return;
        }

        this.pendingQtyRequests.add(requestKey);
        row.classList.add('opacity-70');

        const payload = new URLSearchParams();
        payload.append('nonce', this.ajaxNonce);
        payload.append('list_id', String(listId));
        payload.append('product_id', String(productId));
        payload.append('direction', direction);

        try {
            const response = await postWcAjax('sp_shopping_lists_update_qty', payload);
            if (!response?.success) {
                this.showToast(
                    response?.data?.message || 'Nie udało się zmienić ilości produktu.',
                    'cart'
                );
                return;
            }

            const qtyInput = row.querySelector('[data-shopping-list-qty-value]');
            if (qtyInput instanceof HTMLInputElement) {
                qtyInput.value = String(parseInt(response?.data?.quantity || '1', 10) || 1);
            }

            const lineTotalValue = row.querySelector('[data-shopping-list-line-total-value]');
            if (lineTotalValue instanceof HTMLElement && response?.data?.line_total_html) {
                lineTotalValue.innerHTML = response.data.line_total_html;
            } else {
                const lineTotal = row.querySelector('[data-shopping-list-line-total]');
                if (lineTotal instanceof HTMLElement && response?.data?.line_total_html) {
                    lineTotal.innerHTML = response.data.line_total_html;
                }
            }

            this.showToast(
                response?.data?.message || 'Zmieniono ilość produktu na liście.',
                'cart'
            );
        } catch (error) {
            this.showToast('Nie udało się zmienić ilości produktu.', 'cart');
        } finally {
            this.pendingQtyRequests.delete(requestKey);
            row.classList.remove('opacity-70');
        }
    }

    async handleRemoveItem(button) {
        const row = button.closest('[data-shopping-list-item]');
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const listId = parseInt(row.getAttribute('data-list-id') || '0', 10);
        const productId = parseInt(row.getAttribute('data-product-id') || '0', 10);
        if (!listId || !productId || !this.ajaxNonce) {
            return;
        }

        const requestKey = `${listId}:${productId}`;
        if (this.pendingRemoveRequests.has(requestKey)) {
            return;
        }

        this.pendingRemoveRequests.add(requestKey);
        row.classList.add('opacity-70');
        if ('disabled' in button) {
            button.disabled = true;
        }

        const payload = new URLSearchParams();
        payload.append('nonce', this.ajaxNonce);
        payload.append('list_id', String(listId));
        payload.append('product_id', String(productId));

        try {
            const response = await postWcAjax('sp_shopping_lists_remove_item', payload);
            if (!response?.success) {
                this.showToast(
                    response?.data?.message || 'Nie udało się usunąć produktu z listy.',
                    'cart'
                );
                return;
            }

            row.remove();
            this.showToast(
                response?.data?.message || 'Produkt został usunięty z listy.',
                'cart'
            );

            const itemsContainer = this.pageRoot.querySelector('[data-shopping-list-items]');
            const hasItems = itemsContainer instanceof HTMLElement
                ? itemsContainer.querySelectorAll('[data-shopping-list-item]').length > 0
                : true;

            if (!hasItems && itemsContainer instanceof HTMLElement) {
                const tableHead = this.pageRoot.querySelector('[data-shopping-list-table-head]');
                if (tableHead instanceof HTMLElement) {
                    tableHead.classList.add('hidden');
                }

                itemsContainer.innerHTML = `
                    <div class="rounded-[16px] border border-neutral-100 bg-white p-8 text-neutral-500">
                        Ta lista nie zawiera jeszcze produktów.
                    </div>
                `;
            }
        } catch (error) {
            this.showToast('Nie udało się usunąć produktu z listy.', 'cart');
        } finally {
            this.pendingRemoveRequests.delete(requestKey);
            row.classList.remove('opacity-70');
            if ('disabled' in button) {
                button.disabled = false;
            }
        }
    }

    async handleAddListToCart(form) {
        if (!this.ajaxNonce) {
            this.showToast('Brak tokena bezpieczeństwa. Odśwież stronę i spróbuj ponownie.', 'cart');
            return;
        }

        const listIdInput = form.querySelector('input[name="list_id"]');
        const listId = parseInt(listIdInput?.value || '0', 10);
        if (!listId) {
            this.showToast('Nie udało się odczytać listy zakupowej.', 'cart');
            return;
        }

        if (this.pendingAddListToCartRequests.has(listId)) {
            return;
        }

        const button = form.querySelector('[data-shopping-list-add-to-cart-button]');
        this.pendingAddListToCartRequests.add(listId);
        if (button instanceof HTMLButtonElement) {
            button.disabled = true;
            button.classList.add('opacity-70');
        }

        const payload = new URLSearchParams();
        payload.append('nonce', this.ajaxNonce);
        payload.append('list_id', String(listId));

        try {
            const response = await postWcAjax('sp_shopping_lists_add_list_to_cart', payload);
            if (!response?.success) {
                this.showToast(
                    response?.data?.message || 'Nie udało się dodać produktów do koszyka.',
                    'cart'
                );
                return;
            }

            const data = response?.data || {};

            if (data.fragments) {
                replaceWooFragments(data.fragments);
            }

            if (data.cart_count_html) {
                replaceWooFragments({
                    'span.header-cart-count': data.cart_count_html,
                });
            }

            if (data.mini_cart_wrapper_html) {
                replaceWooFragments({
                    '#mini-cart-wrapper': data.mini_cart_wrapper_html,
                });
            }

            if (data.mini_cart_html) {
                const wrapper = document.querySelector('#mini-cart-wrapper');
                if (wrapper) {
                    wrapper.innerHTML = data.mini_cart_html;
                } else {
                    const container = document.querySelector('.mini-cart__wrapper > div');
                    if (container) {
                        container.innerHTML = data.mini_cart_html;
                    }
                }
            }

            if (window.jQuery) {
                const $button = button ? window.jQuery(button) : null;
                const $body = window.jQuery(document.body);

                $body.trigger('added_to_cart', [
                    data.fragments || {},
                    data.cart_hash || '',
                    $button,
                ]);

                if (data.fragments) {
                    $body.trigger('wc_fragments_refreshed', [
                        data.fragments,
                    ]);
                }

                $body.trigger('wc_fragment_refresh');
            }

            this.showToast(
                response?.data?.message || 'Dodano produkty z listy do koszyka.',
                'cart'
            );
        } catch (error) {
            this.showToast(
                this.resolveAjaxErrorMessage(
                    error,
                    'Nie udało się dodać produktów do koszyka.'
                ),
                'cart'
            );
        } finally {
            this.pendingAddListToCartRequests.delete(listId);
            if (button instanceof HTMLButtonElement) {
                button.disabled = false;
                button.classList.remove('opacity-70');
            }
        }
    }

    resolveAjaxErrorMessage(error, fallbackMessage) {
        const raw = String(error?.message || '').trim();
        if (!raw) {
            return fallbackMessage;
        }

        if (raw === '-1') {
            return 'Sesja wygasła lub token jest nieprawidłowy. Odśwież stronę i spróbuj ponownie.';
        }

        if (raw.includes('Failed to fetch')) {
            return 'Nie udało się połączyć z endpointem AJAX.';
        }

        if (raw.includes('Unexpected token')) {
            return 'Serwer zwrócił nieprawidłową odpowiedź (nie JSON).';
        }

        if (raw.startsWith('<!DOCTYPE') || raw.startsWith('<html')) {
            return 'Serwer zwrócił stronę HTML zamiast odpowiedzi AJAX.';
        }

        if (raw.length > 220) {
            return `${raw.slice(0, 220)}...`;
        }

        return raw;
    }

    showToast(text, id = 'cart') {
        document.body.dispatchEvent(
            new CustomEvent('sp:toast', {
                detail: {
                    id,
                    text,
                },
            })
        );
    }

    open(modal) {
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        const panel = modal.querySelector('[data-shopping-lists-create-modal-panel], [data-shopping-lists-delete-modal-panel]');
        const closeTimer = this.closeTimers.get(modal);
        if (closeTimer) {
            window.clearTimeout(closeTimer);
        }

        modal.classList.remove('hidden');
        modal.classList.add('flex');
        window.requestAnimationFrame(() => {
            modal.classList.remove('opacity-0');
            modal.classList.add('opacity-100');

            if (panel instanceof HTMLElement) {
                panel.classList.remove('opacity-0', 'translate-y-2', 'scale-[0.98]');
                panel.classList.add('opacity-100', 'translate-y-0', 'scale-100');
            }
        });

        document.body.classList.add('overflow-hidden');
    }

    close(modal) {
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        const panel = modal.querySelector('[data-shopping-lists-create-modal-panel], [data-shopping-lists-delete-modal-panel]');
        modal.classList.remove('opacity-100');
        modal.classList.add('opacity-0');

        if (panel instanceof HTMLElement) {
            panel.classList.remove('opacity-100', 'translate-y-0', 'scale-100');
            panel.classList.add('opacity-0', 'translate-y-2', 'scale-[0.98]');
        }

        const timer = window.setTimeout(() => {
            modal.classList.add('hidden');
            modal.classList.remove('flex');
            this.closeTimers.delete(modal);

            if (!this.isOpen(this.createModal) && !this.isOpen(this.deleteModal)) {
                document.body.classList.remove('overflow-hidden');
            }
        }, 200);
        this.closeTimers.set(modal, timer);
    }

    isOpen(modal) {
        return modal instanceof HTMLElement && !modal.classList.contains('hidden');
    }
}

new ShoppingListsPageModals();
